import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { Body, BodyColor, BodyType, Heading, TypographyColor, TypographyType } from "../../ui-elements";
import { selectedCardState } from "../../states/DashboardState";
import { useRecoilValue } from "recoil";

interface StackRankDropdownProps {
    isOpen: boolean;
    handleListOpen: () => void;
    stackRankOptions: StackRankOption[];
    selectedOptions: string[];
    selectedGeoType: string;
    setSelectedDashboardStackRankKeys: React.Dispatch<React.SetStateAction<any>>;
    selectedDashboardStackRankKeys: any;

}

interface StackRankOption {
    label: string;
    key: string;
    isSelected: boolean;
    children?: StackRankOption[];
    show_children: boolean;
}

export const StackRankDropdownDashboard = ({
    isOpen,
    handleListOpen,
    stackRankOptions,
    selectedOptions,
    selectedGeoType,
    selectedDashboardStackRankKeys,
    setSelectedDashboardStackRankKeys
}: StackRankDropdownProps) => {
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const selectedCardType = useRecoilValue(selectedCardState);
    const [searchParams] = useSearchParams();
    const isCatchment = !!searchParams.get('catchment');

    useEffect(() => {
        if (selectedDashboardStackRankKeys.length === 0) {
            setSelectedDashboardStackRankKeys((prev: any) => {
                const updated = { ...prev, [selectedGeoType]: ['EH'] };
                localStorage.setItem("selectedDashboardStackRankKeys", JSON.stringify(updated));
                return updated;
            });
        }
    }, [selectedDashboardStackRankKeys, setSelectedDashboardStackRankKeys]);

    useEffect(() => {
        if (isOpen) {
            if (selectedOptions.length == 0) {
                setSelectedDashboardStackRankKeys((prev: any) => {
                    const updated = { ...prev, [selectedGeoType]: ['EH'] };
                    localStorage.setItem("selectedDashboardStackRankKeys", JSON.stringify(updated));
                    return updated;
                });
                return;
            }
            // setSelectedDashboardStackRankKeys(selectedOptions);
        }
    }, [isOpen, selectedOptions]);


    const toggleExpand = (key: string) => {
        setExpandedKey(prev => (prev === key ? null : key));
    };

    const isChecked = (key: string) => {
        const foundParent = stackRankOptions.find(item => item.key === key);

        if (foundParent && foundParent.children) {
            return foundParent.children.some(child => selectedDashboardStackRankKeys?.includes(child.key));
        }
        return false;
    }

    const toggleParent = (parent: StackRankOption) => {
        const isParentSelected = isChecked(parent.key);

        if (isParentSelected) {
            setSelectedDashboardStackRankKeys((prev: any) => {
                const updated = {
                    ...prev,
                    [selectedGeoType]: prev[selectedGeoType].filter((k: string) =>
                        !(parent.children && parent.children.some(child => child.key === k))
                    )
                };
                localStorage.setItem("selectedDashboardStackRankKeys", JSON.stringify(updated));
                return updated;
            });
        } else if (parent.children && parent.children?.length > 0 && parent.children[0]) {
            setSelectedDashboardStackRankKeys((prev: any) => {
                const updated = {
                    ...prev,
                    [selectedGeoType]: [...prev[selectedGeoType], parent.children![0].key]
                };
                localStorage.setItem("selectedDashboardStackRankKeys", JSON.stringify(updated));
                return updated;
            });
        }
    };


    const handleChildSelect = (parent: StackRankOption, childKey: string) => {
        setSelectedDashboardStackRankKeys((prev: any) => {
            const updated = {
                ...prev,
                [selectedGeoType]: [...prev[selectedGeoType].filter((k: string) => !(parent.children && parent.children.some(child => child.key === k))), childKey]
            };
            localStorage.setItem("selectedDashboardStackRankKeys", JSON.stringify(updated));
            return updated;
        });
    };

    const getParentLabel = (parent: any) => {
        const foundChild = parent?.children?.find((child: any) => selectedDashboardStackRankKeys?.includes(child.key));
        return foundChild ? foundChild.label : parent.label;
    }
    const getDisplayCount = () => {
        if (!selectedCardType.cTVData && (selectedGeoType === 'Subdistricts' || selectedGeoType === 'Pincode' || selectedGeoType === 'Villages')) {
            // Exclude CTV
            return selectedOptions?.filter((opt: string) => opt !== 'CTV').length || 0;
        }
        return selectedOptions?.length || 0;
    };

    return (
        <div className="stack-rank-dropdown-container position-relative h-100">
            {/* Header */}
            <div
                className="d-flex justify-content-between align-items-center cursor-pointer padding-top-bottom-2 padding-left-right-5 poi-category width-17-688vw h-100"
                onClick={handleListOpen}
            >
                <Heading
                    title={`Stackrank by (${getDisplayCount().toString().padStart(2, '0')})`}
                    type={TypographyType.h5}
                    colour={TypographyColor.secondary}
                    classname="icon-gray-0"
                />
                {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
            </div>

            {isOpen && (
                <div className="poi-category-list-dashboard"
                    style={{ boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 4px", height: "fit-content", zIndex: 10 }}>
                    {/* Main List */}
                    {stackRankOptions?.map(option => {
                        if (option.key === 'ctv' && (selectedGeoType === 'Subdistricts' || selectedGeoType === 'Pincode' || selectedGeoType === 'Villages' || isCatchment)) return null;
                        return (
                            <div key={option.key} className="padding-left-right-5 padding-top-bottom-2">
                                {/* Parent */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            checked={isChecked(option.key)}
                                            onChange={() => toggleParent(option)}
                                        />
                                        <Body type={BodyType.p3} color={BodyColor.primary} classname="ms-2">
                                            {getParentLabel(option)}
                                        </Body>
                                    </div>

                                    {option.children && option.show_children && (
                                        <div onClick={() => toggleExpand(option.key)} className="cursor-pointer">
                                            {expandedKey === option.key ? <IoIosArrowUp /> : <IoIosArrowDown />}
                                        </div>
                                    )}
                                </div>

                                {/* Child (radio) */}
                                {expandedKey === option.key && option.show_children && option.children?.map(child => (
                                    <div key={child.key} className="d-flex align-items-center padding-left-5 mt-2">
                                        <input
                                            type="radio"
                                            name={`radio-${option.key}`}
                                            checked={selectedDashboardStackRankKeys?.includes(child.key)}
                                            onChange={() => handleChildSelect(option, child.key)}
                                        />
                                        <Body type={BodyType.p4} color={BodyColor.secondary} classname="ms-2">
                                            {child.label}
                                        </Body>
                                    </div>
                                ))}
                            </div>
                        )
                    })}

                </div>
            )}
        </div>
    );
};
